import { LinearProgress, Typography } from "@mui/material";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import Btn from "./Btn";
import DynamicSkillSelector from "./DynamicSkillSelector";
import angelWingsIcon from "../assets/svg/angel-wings.svg";

const WINGS_STYLE = { width: 10, filter: "brightness(0) invert(1)", opacity: 0.8, verticalAlign: "middle" };

export default function MjPlayerCharacterCard({
  char, gauges, draconique, celeste,
  isKO, isDead,
  selected, toggleSelected, currentTurnCharId,
  selectorVisibleFor, setSelectorVisibleFor,
  handleFinalSelection, skillScores,
  openAlertDialogFor,
}) {
  const n = (v) => Number(v);
  const clamp = (v) => Math.min(100, Math.max(0, v));

  const currentManaVital   = n(gauges?.currentManaVital   ?? char.ManaVital_character);
  const currentStamina     = n(gauges?.currentStamina     ?? char.Stamina_character);
  const currentManaAir     = n(gauges?.currentManaAir     ?? char.ManaAir_character);
  const currentManaEau     = n(gauges?.currentManaEau     ?? char.ManaEau_character);
  const currentManaTerre   = n(gauges?.currentManaTerre   ?? char.ManaTerre_character);
  const currentManaFeu     = n(gauges?.currentManaFeu     ?? char.ManaFeu_character);
  const currentManaVolonte = n(gauges?.currentManaVolonte ?? char.ManaVolonte_character);

  const clampedManaVital   = clamp((currentManaVital   / char.ManaVital_character)   * 100);
  const clampedStamina     = clamp((currentStamina     / char.Stamina_character)     * 100);
  const clampedManaAir     = clamp((currentManaAir     / char.ManaAir_character)     * 100);
  const clampedManaEau     = clamp((currentManaEau     / char.ManaEau_character)     * 100);
  const clampedManaTerre   = clamp((currentManaTerre   / char.ManaTerre_character)   * 100);
  const clampedManaFeu     = clamp((currentManaFeu     / char.ManaFeu_character)     * 100);
  const clampedManaVolonte = clamp((currentManaVolonte / char.ManaVolonte_character) * 100);

  const ko   = isKO(char);
  const dead = isDead(char);
  const isCurrentTurn = currentTurnCharId === char.ID_character;
  const isSelected    = selected.has(char.ID_character);

  const celestePct = (field) => celeste ? Number(celeste[field]) : 0;

  const Wings = ({ field }) => {
    const pct = celestePct(field);
    if (!pct) return null;
    return (
      <span style={{ display: "block", textAlign: "center", whiteSpace: "nowrap", marginTop: 2, fontSize: "0.72rem" }}>
        <img src={angelWingsIcon} style={WINGS_STYLE} alt="" />
        {" "}{pct}%{" "}
        <img src={angelWingsIcon} style={{ ...WINGS_STYLE, transform: "scaleX(-1)" }} alt="" />
      </span>
    );
  };

  // Mana slot : jauge normale + jauge draconique optionnelle côte à côte
  // barHeight : hauteur vw du conteneur de jauge (ex "18vw")
  const ManaSlot = ({ label, value, clamped, barSx, celesteField, dracCurrentKey, dracMaxKey, barHeight = "20vw" }) => {
    const dracCurr = draconique ? n(draconique[dracCurrentKey] ?? 0) : 0;
    const dracMax  = draconique ? n(draconique[dracMaxKey]    ?? 1) : 1;
    const dracPct  = clamp((dracCurr / Math.max(1, dracMax)) * 100);
    const bh = draconique ? barHeight : "25vw";

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
          {/* Jauge normale */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ position: "relative", width: "8px", height: bh }}>
              <LinearProgress variant="determinate" value={clamped}
                sx={{ position: "absolute", bottom: 0, left: "8px", width: bh, height: "8px", borderRadius: "25px",
                      transformOrigin: "bottom left", transform: "rotate(-90deg)", ...barSx }} />
            </div>
            <p style={{ color: "lightblue", fontSize: "0.85rem", textAlign: "center", margin: "4px 0 0", whiteSpace: "nowrap" }}>
              {value}
            </p>
            <Wings field={celesteField} />
          </div>
          {/* Jauge draconique — mêmes couleurs que la jauge normale */}
          {draconique && dracCurrentKey && (
            <>
              <div style={{ width: 1, background: "rgba(255,255,255,0.15)", alignSelf: "stretch" }} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ position: "relative", width: "8px", height: bh }}>
                  <LinearProgress variant="determinate" value={dracPct}
                    sx={{ position: "absolute", bottom: 0, left: "8px", width: bh, height: "8px", borderRadius: "25px",
                          transformOrigin: "bottom left", transform: "rotate(-90deg)", ...barSx }} />
                </div>
                <p style={{ color: "lightblue", fontSize: "0.8rem", textAlign: "center", margin: "4px 0 0", whiteSpace: "nowrap" }}>
                  {dracCurr}/{dracMax}
                </p>
              </div>
            </>
          )}
        </div>
        <p style={{ color: "lightblue", fontSize: "0.8rem", textAlign: "center", margin: 0 }}>{label}</p>
      </div>
    );
  };

  // Quand draconique actif : 2 par ligne (Air+Eau, Terre+Feu) ; sinon : 3 par ligne (Air/Eau/Terre, Feu/Volonté)
  const hasDraco = !!draconique;

  return (
    <div style={{
      border: isCurrentTurn ? "2px solid #ffa726" : isSelected ? "2px solid #90caf9" : "1px solid rgba(255,255,255,0.2)",
      borderRadius: 10, padding: 12,
      background: isCurrentTurn ? "rgba(255,167,38,0.1)" : isSelected ? "rgba(144,202,249,0.1)" : "rgba(0,0,0,0.3)",
      transition: "border 0.2s, background 0.2s",
      position: "relative", zIndex: 1,
      opacity: ko && !dead ? 0.4 : 1,
    }}>
      {dead && (
        <div style={{ position: "absolute", inset: 0, borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.78)", zIndex: 3, gap: 4, pointerEvents: "none" }}>
          <span style={{ fontSize: "2rem" }}>💀</span>
          <span style={{ color: "#f87171", fontWeight: "bold", letterSpacing: "0.3em", fontSize: "1.1rem" }}>MORT</span>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <p style={{ color: "lightblue", fontSize: "1.1rem", fontWeight: "bold", margin: 0 }}>{char.Name_character}</p>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <span style={{ color: "lightblue", fontSize: "0.85rem" }}>{isSelected ? "Sélectionné ✓" : "Sélectionner"}</span>
          <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(char.ID_character)} style={{ width: 22, height: 22, cursor: "pointer" }} />
        </label>
      </div>

      {hasDraco ? (
        <>
          {/* Ligne 1 draconique : Air + Eau */}
          <div style={{ display: "flex", justifyContent: "space-around", gap: 8, marginBottom: 16 }}>
            <ManaSlot label="Air" value={currentManaAir} clamped={clampedManaAir}
              barSx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#14b8a6" } }}
              celesteField="CelesteManaAir"
              dracCurrentKey="CurrentManaAirDraconique" dracMaxKey="ManaAirDraconiqueMax" />
            <ManaSlot label="Eau" value={currentManaEau} clamped={clampedManaEau}
              barSx={{}}
              celesteField="CelesteManaEau"
              dracCurrentKey="CurrentManaEauDraconique" dracMaxKey="ManaEauDraconiqueMax" />
          </div>
          {/* Ligne 2 draconique : Terre + Feu */}
          <div style={{ display: "flex", justifyContent: "space-around", gap: 8, marginBottom: 16 }}>
            <ManaSlot label="Terre" value={currentManaTerre} clamped={clampedManaTerre}
              barSx={{}}
              celesteField="CelesteManaTerre"
              dracCurrentKey="CurrentManaTerreDraconique" dracMaxKey="ManaTerreDraconiqueMax" />
            <ManaSlot label="Feu" value={currentManaFeu} clamped={clampedManaFeu}
              barSx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#fb7185" }, backgroundColor: "#991b1b" }}
              celesteField="CelesteManafeu"
              dracCurrentKey="CurrentManaFeuDraconique" dracMaxKey="ManaFeuDraconiqueMax" />
          </div>
          {/* Volonté — pas draconique */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <ManaSlot label="Volonté" value={currentManaVolonte} clamped={clampedManaVolonte}
              barSx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#a855f7" }, backgroundColor: "#6b21a8" }}
              celesteField="CelesteManaVolonte"
              dracCurrentKey={null} dracMaxKey={null} dracColor={null} />
          </div>
        </>
      ) : (
        <>
          {/* Layout sans draconique — original */}
          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 8 }}>
            {[
              { label: "Air",   value: currentManaAir,   clamped: clampedManaAir,   barSx: { "& .MuiLinearProgress-bar": { backgroundColor: "#14b8a6" } }, celesteField: "CelesteManaAir" },
              { label: "Eau",   value: currentManaEau,   clamped: clampedManaEau,   barSx: {}, celesteField: "CelesteManaEau" },
              { label: "Terre", value: currentManaTerre, clamped: clampedManaTerre, barSx: {}, celesteField: "CelesteManaTerre" },
            ].map(({ label, value, clamped, barSx, celesteField }) => (
              <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: "30%" }}>
                <div style={{ position: "relative", width: "8px", height: "25vw" }}>
                  <LinearProgress variant="determinate" value={clamped}
                    sx={{ position: "absolute", bottom: 0, left: "8px", width: "25vw", height: "8px", borderRadius: "25px", transformOrigin: "bottom left", transform: "rotate(-90deg)", ...barSx }} />
                </div>
                <p style={{ color: "lightblue", fontSize: "0.85rem", textAlign: "center", margin: 0, whiteSpace: "nowrap" }}>
                  {label}<br />{value}
                </p>
                <Wings field={celesteField} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 16 }}>
            {[
              { label: "Feu",     value: currentManaFeu,     clamped: clampedManaFeu,     barSx: { "& .MuiLinearProgress-bar": { backgroundColor: "#fb7185" }, backgroundColor: "#991b1b" }, celesteField: "CelesteManafeu" },
              { label: "Volonté", value: currentManaVolonte, clamped: clampedManaVolonte, barSx: { "& .MuiLinearProgress-bar": { backgroundColor: "#a855f7" }, backgroundColor: "#6b21a8" }, celesteField: "CelesteManaVolonte" },
            ].map(({ label, value, clamped, barSx, celesteField }) => (
              <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: "30%" }}>
                <div style={{ position: "relative", width: "8px", height: "25vw" }}>
                  <LinearProgress variant="determinate" value={clamped}
                    sx={{ position: "absolute", bottom: 0, left: "8px", width: "25vw", height: "8px", borderRadius: "25px", transformOrigin: "bottom left", transform: "rotate(-90deg)", ...barSx }} />
                </div>
                <p style={{ color: "lightblue", fontSize: "0.85rem", textAlign: "center", margin: 0, whiteSpace: "nowrap" }}>
                  {label}<br />{value}
                </p>
                <Wings field={celesteField} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Vital + Stamina — jamais draconique */}
      <div style={{ display: "flex", justifyContent: "space-around", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <p style={{ color: "lightblue", fontSize: "0.9rem", margin: 0 }}>
            Mana Vital
            {celestePct("CelesteManaVital") > 0 && <Wings field="CelesteManaVital" />}
          </p>
          <div style={{ width: "40dvw" }}>
            <CircularProgressbar value={clampedManaVital} circleRatio={0.5} text={`${currentManaVital} pts`}
              styles={buildStyles({ rotation: 0.75, strokeLinecap: "butt", textSize: "1em", pathTransitionDuration: 0.5, pathColor: "red", textColor: "#f88", trailColor: "#af8c8d" })} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <p style={{ color: "lightblue", fontSize: "0.9rem", margin: 0 }}>Stamina</p>
          <div style={{ width: "40dvw" }}>
            <CircularProgressbar value={clampedStamina} circleRatio={0.5} text={`${currentStamina} pts`}
              styles={buildStyles({ rotation: 0.75, strokeLinecap: "butt", textSize: "1em", pathTransitionDuration: 0.5, pathColor: "#42d750", textColor: "#f88", trailColor: "#cfe9d2" })} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
        <Btn onClick={() => setSelectorVisibleFor(selectorVisibleFor === char.ID_character ? null : char.ID_character)} msg="Compétence" />
        <Btn msg="Alerte" onClick={() => openAlertDialogFor([char.ID_character])} />
      </div>
      {selectorVisibleFor === char.ID_character && (
        <DynamicSkillSelector onFinalSelect={(path) => handleFinalSelection(char.ID_character, path)} />
      )}
      {skillScores[char.ID_character] && (
        <p style={{ marginTop: 8, fontSize: "1.1rem", color: "lightblue" }}>
          {skillScores[char.ID_character].label} : {skillScores[char.ID_character].valeur}
        </p>
      )}
    </div>
  );
}
